class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.isMegaMenu = this.mainDetailsToggle.classList.contains('mega-menu');
    this.hoverMediaQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeDelayTimer = undefined;
    this.closeAnimationTimer = undefined;
    this.openAnimationFrame = undefined;
    this.openTransitionFrame = undefined;
    this.isPointerClick = false;
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onSummaryPointerDown = this.onSummaryPointerDown.bind(this);
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    if (!this.isMegaMenu) return;

    this.addEventListener('mouseenter', this.onPointerEnter);
    this.addEventListener('mouseleave', this.onPointerLeave);
    this.summary.addEventListener('pointerdown', this.onSummaryPointerDown);
    this.summary.addEventListener('click', this.onSummaryClick);
    this.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    if (!this.isMegaMenu) return;

    this.removeEventListener('mouseenter', this.onPointerEnter);
    this.removeEventListener('mouseleave', this.onPointerLeave);
    this.summary.removeEventListener('pointerdown', this.onSummaryPointerDown);
    this.summary.removeEventListener('click', this.onSummaryClick);
    this.removeEventListener('keydown', this.onKeyDown);
    window.clearTimeout(this.closeDelayTimer);
    window.clearTimeout(this.closeAnimationTimer);
    window.cancelAnimationFrame(this.openAnimationFrame);
    window.cancelAnimationFrame(this.openTransitionFrame);
  }

  /*
    The panel's reveal is a CSS transition whose length is a section setting, and
    the close has to hold the element in the DOM for exactly that long. Reading
    it back from the custom property keeps the two from drifting apart.
  */
  get animationDuration() {
    const declared = getComputedStyle(this).getPropertyValue('--mega-menu-duration').trim();
    const milliseconds = declared.endsWith('ms') ? parseFloat(declared) : parseFloat(declared) * 1000;
    return Number.isFinite(milliseconds) && milliseconds >= 0 ? milliseconds : 360;
  }

  onPointerEnter() {
    if (!this.hoverMediaQuery.matches) return;

    window.clearTimeout(this.closeDelayTimer);
    window.clearTimeout(this.closeAnimationTimer);
    window.cancelAnimationFrame(this.openAnimationFrame);
    window.cancelAnimationFrame(this.openTransitionFrame);

    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && menu.mainDetailsToggle?.open) menu.close();
    });

    if (this.mainDetailsToggle.open) {
      this.mainDetailsToggle.classList.remove('is-opening', 'is-closing');
      this.summary.setAttribute('aria-expanded', 'true');
      return;
    }

    this.mainDetailsToggle.classList.add('is-opening');
    this.mainDetailsToggle.classList.remove('is-closing');
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');

    if (this.reduceMotionQuery.matches) {
      this.mainDetailsToggle.classList.remove('is-opening');
      return;
    }

    this.openAnimationFrame = window.requestAnimationFrame(() => {
      this.openTransitionFrame = window.requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.remove('is-opening');
      });
    });
  }

  onPointerLeave() {
    if (!this.hoverMediaQuery.matches) return;

    this.isPointerClick = false;
    window.clearTimeout(this.closeDelayTimer);
    this.closeDelayTimer = window.setTimeout(() => this.animateClose(), 80);
  }

  onSummaryClick(event) {
    if (event.target.closest('a[href]')) {
      this.isPointerClick = false;
      return;
    }

    if (this.hoverMediaQuery.matches && this.isPointerClick) event.preventDefault();
    this.isPointerClick = false;
  }

  onSummaryPointerDown() {
    this.isPointerClick = true;
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.mainDetailsToggle.open) return;

    event.preventDefault();
    this.close();
    this.summary.focus();
  }

  animateClose() {
    if (!this.mainDetailsToggle.open) return;

    window.cancelAnimationFrame(this.openAnimationFrame);
    window.cancelAnimationFrame(this.openTransitionFrame);
    this.mainDetailsToggle.classList.remove('is-opening');

    if (this.reduceMotionQuery.matches) {
      this.close();
      return;
    }

    this.mainDetailsToggle.classList.add('is-closing');
    this.summary.setAttribute('aria-expanded', 'false');
    window.clearTimeout(this.closeAnimationTimer);
    this.closeAnimationTimer = window.setTimeout(() => {
      this.close();
    }, this.animationDuration);
  }

  close() {
    window.clearTimeout(this.closeDelayTimer);
    window.clearTimeout(this.closeAnimationTimer);
    window.cancelAnimationFrame(this.openAnimationFrame);
    window.cancelAnimationFrame(this.openTransitionFrame);
    this.mainDetailsToggle.classList.remove('is-opening', 'is-closing');
    super.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.open.toString());

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
